/**
 * Карточка арендатора внутри «Объём 3D» (S8, референс
 * maplibre-3d-building-visualization (3) → TenantModal): та же информация, что
 * на обычной карте, плюс действия «Маршрут» и «QR».
 *
 * «Маршрут» ведёт себя по обстановке (запрос владельца 28.07):
 *  - цель в ТОМ ЖЕ здании, где мы сейчас смотрим объём → путь рисуется прямо
 *    здесь, не выходя из 3D;
 *  - иначе → закрываем объём и строим обычный маршрут на карте.
 */
import { getLang, tr } from '@app/i18n'
import { localized, type Venue } from '@app/rvf'
import { selectTarget } from '../actions'
import { qrPassTargetId, venue, volume3dUnitId } from '../state'
import type { TenantModel } from '../volume3d/model'
import { zoneColor } from '../volume3d/zone-color'
import { categoryLabel } from './labels'

interface CabinInfo {
  no: string
  type: string
  side: string
  /** Своя площадь каюты: зона в сцене — плитка для читаемости, а не метраж. */
  areaM2: number | null
  guests: number | null
}

/** Каюта: номер, тип, борт, метраж и вместимость (S8, судно). */
function cabinInfoOf(v: Venue, id: string): CabinInfo | null {
  const poi = v.pois.find((p) => p.id === id)
  const attrs = poi?.attrs as Record<string, unknown> | undefined
  const no = attrs?.['cabin_no']
  if (typeof no !== 'string') return null
  const num = (key: string): number | null => {
    const raw = Number(attrs?.[key])
    return Number.isFinite(raw) ? raw : null
  }
  return {
    no,
    type: String(attrs?.['cabin_type'] ?? ''),
    side: String(attrs?.['side'] ?? ''),
    areaM2: num('area_m2'),
    guests: num('guests'),
  }
}

/** Первая буква названия — как логотип-заглушка в референсе. */
function initial(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '·'
}

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`
}

/** Описание арендатора из карточки/атрибутов — то же, что на обычной карте. */
function descriptionOf(v: Venue, id: string): string {
  const lang = getLang()
  const poi = v.pois.find((p) => p.id === id)
  const unit = v.units.find((u) => u.id === id)
  const src = (poi ?? unit)?.attrs as Record<string, unknown> | undefined
  const desc = src?.['description']
  if (desc && typeof desc === 'object') {
    return localized(desc as Record<string, string>, lang, v.venue.default_language)
  }
  return typeof desc === 'string' ? desc : ''
}

export function Volume3dTenantCard({
  tenant,
  areaM2,
  floor,
  onClose,
  onRouteInside,
  canRouteInside,
}: {
  tenant: TenantModel
  areaM2: number | null
  floor: number
  onClose: () => void
  /** Построить маршрут прямо в объёме (цель в этом же здании). */
  onRouteInside: () => void
  canRouteInside: boolean
}) {
  const v = venue.value
  const color = hex(zoneColor(tenant.id))
  const description = v ? descriptionOf(v, tenant.id) : ''
  const cabin = v ? cabinInfoOf(v, tenant.id) : null
  // У каюты метраж свой: зона в сцене — плитка для читаемости плана.
  const shownArea = cabin?.areaM2 ?? areaM2

  return (
    <div class="v3d-card" role="dialog" aria-label={tenant.name}>
      <div
        class="v3d-card-cover"
        style={{ background: `linear-gradient(135deg, ${color}, #0b1220)` }}
      >
        <div class="v3d-card-logo" style={{ background: color }}>
          {initial(tenant.name)}
        </div>
        <span class="v3d-card-cat">{categoryLabel(tenant.category)}</span>
        <button
          type="button"
          class="v3d-card-close"
          onClick={onClose}
          aria-label={tr('common.close')}
        >
          ✕
        </button>
      </div>

      <div class="v3d-card-body">
        <h3 class="v3d-card-title">{tenant.name}</h3>
        {description && <p class="v3d-card-desc">{description}</p>}
        {cabin && (
          <p class="v3d-card-desc">
            {tr(`cabin.${cabin.type}` as never) || cabin.type}
            {cabin.side ? ` · ${tr(`cabin.${cabin.side}` as never) || cabin.side}` : ''}
          </p>
        )}

        <div class="v3d-card-kpi">
          <div>
            <span class="v3d-kpi-label">{tr('volume3d.kpi_floor')}</span>
            <span class="v3d-kpi-value">{floor}</span>
          </div>
          {cabin && (
            <div>
              <span class="v3d-kpi-label">{tr('volume3d.kpi_cabin')}</span>
              <span class="v3d-kpi-value">{cabin.no}</span>
            </div>
          )}
          {shownArea !== null && (
            <div>
              <span class="v3d-kpi-label">{tr('volume3d.kpi_area')}</span>
              <span class="v3d-kpi-value">
                {shownArea} {tr('unit.m')}²
              </span>
            </div>
          )}
          {cabin?.guests != null && (
            <div>
              <span class="v3d-kpi-label">{tr('volume3d.kpi_guests')}</span>
              <span class="v3d-kpi-value">{cabin.guests}</span>
            </div>
          )}
        </div>

        <div class="v3d-card-actions">
          <button
            type="button"
            class="v3d-btn v3d-btn-primary"
            onClick={() => {
              if (canRouteInside) {
                onRouteInside()
                return
              }
              // Цель в другом месте — уходим на карту и строим обычный маршрут.
              volume3dUnitId.value = null
              selectTarget(tenant.id)
            }}
          >
            {tr('target.route')}
          </button>
          <button
            type="button"
            class="v3d-btn"
            aria-label={tr('qrpass.kicker')}
            onClick={() => {
              qrPassTargetId.value = tenant.id
            }}
          >
            QR
          </button>
        </div>
      </div>
    </div>
  )
}
